import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import api from '../functions/api';

// DAS-2568: the owner's prime objective — stored in User_Prime_Objective,
// editable here, and read by LLM pipelines (prime_objective.py). Narrative
// companion: docs/Prime_Objective.md.
const Prime_Objective = () => {
  const userId = useSelector((state) => state.auth.userId);
  const [objective, setObjective] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    fetchObjective();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchObjective = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/prime-objective/${userId}`);
      setObjective(response.data);
      setEditText(response.data.objective_text || '');
      setError(null);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        // No objective stored yet — allow creating one from an empty editor.
        setObjective(null);
        setEditText('');
        setEditing(true);
        setError(null);
      } else {
        setError('Failed to fetch prime objective');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const response = await api.put(`/prime-objective/${userId}`, {
        objective_text: editText
      });
      setObjective(response.data);
      setEditing(false);
      setError(null);
    } catch (err) {
      setError('Failed to save prime objective');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setEditText(objective ? objective.objective_text : '');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
        Prime Objective
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The application's guiding goal. LLM analyses and agents read this text
        when generating and prioritizing advice — keep it current.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        {editing ? (
          <TextField
            fullWidth
            multiline
            rows={12}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            variant="outlined"
            placeholder="Describe your prime objective with this application…"
            sx={{ fontFamily: 'monospace' }}
          />
        ) : (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              backgroundColor: '#f8f9fa',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6,
              fontSize: '14px'
            }}
          >
            {objective ? objective.objective_text : 'No objective stored yet.'}
          </Paper>
        )}

        {objective && !editing && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Last updated: {new Date(objective.updated_at).toLocaleString()}
          </Typography>
        )}
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {editing ? (
          <>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={loading || !editText.trim()}
            >
              Save
            </Button>
          </>
        ) : (
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
            Edit Objective
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default Prime_Objective;
